import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '../cn';

type MiddleEllipsisProps = {
  text: string;
  className?: string;
  /** How many characters to preserve at the end when truncating */
  endChars?: number;
};

/**
 * Truncates text in the middle with ellipsis when it overflows its container.
 * Shows "Start of text…end" instead of "Start of text..."
 */
export default function MiddleEllipsis({ text, className, endChars = 4 }: MiddleEllipsisProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [truncatedText, setTruncatedText] = useState(text);

  // Use layout effect to measure synchronously before paint
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      // Find parent with constrained width (the one with overflow:hidden)
      let measureEl: HTMLElement | null = container.parentElement;
      while (measureEl && getComputedStyle(measureEl).overflow !== 'hidden') {
        measureEl = measureEl.parentElement;
      }
      if (!measureEl) measureEl = container.parentElement;
      if (!measureEl) return;

      const availableWidth = measureEl.clientWidth;
      if (availableWidth <= 0) return;

      // Create a temporary span for measuring text
      const tempSpan = document.createElement('span');
      const styles = window.getComputedStyle(container);
      tempSpan.style.font = styles.font;
      tempSpan.style.fontSize = styles.fontSize;
      tempSpan.style.fontWeight = styles.fontWeight;
      tempSpan.style.fontFamily = styles.fontFamily;
      tempSpan.style.letterSpacing = styles.letterSpacing;
      tempSpan.style.position = 'absolute';
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.whiteSpace = 'nowrap';
      document.body.appendChild(tempSpan);

      // Check if full text fits
      tempSpan.textContent = text;
      const fullWidth = tempSpan.offsetWidth;

      if (fullWidth <= availableWidth) {
        document.body.removeChild(tempSpan);
        setTruncatedText(text);
        return;
      }

      // Binary search to find the right truncation point
      const ellipsis = '…';
      const safeEndChars = Math.min(endChars, Math.floor(text.length / 2));
      const end = text.slice(-safeEndChars);
      let low = 0;
      let high = text.length - safeEndChars;
      let bestFit = ellipsis + end;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const truncated = text.slice(0, mid) + ellipsis + end;
        tempSpan.textContent = truncated;

        if (tempSpan.offsetWidth <= availableWidth) {
          bestFit = truncated;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      document.body.removeChild(tempSpan);
      setTruncatedText(bestFit);
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container.parentElement || container);

    return () => resizeObserver.disconnect();
  }, [text, endChars]);

  // Also re-measure when component updates (in case parent width changes)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Delayed re-measure to catch layout changes
    const timer = setTimeout(() => {
      const event = new Event('resize');
      window.dispatchEvent(event);
    }, 100);

    return () => clearTimeout(timer);
  }, [text]);

  return (
    <span ref={containerRef} className={cn('whitespace-nowrap', className)}>
      {truncatedText}
    </span>
  );
}
